import React from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface TiebreakerModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerData: any;
}

export const TiebreakerModal: React.FC<TiebreakerModalProps> = ({ isOpen, onClose, playerData }) => {
  if (!isOpen || !playerData) return null;

  const tb = playerData.tiebreakers || { setsWon: 0, setsLost: 0, gamesWon: 0, gamesLost: 0, tbWon: 0, tbLost: 0 };
  const matchesDiff = playerData.matchesWon - playerData.matchesLost;
  const setsDiff = tb.setsWon - tb.setsLost;
  const gamesDiff = tb.gamesWon - tb.gamesLost;
  const tbDiff = tb.tbWon - tb.tbLost;
  
  const formatDate = (isoString: string) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    return d.toLocaleDateString('pt-BR');
  };

  const modalContent = (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      
      <div style={{ position: 'relative', background: '#fff', width: '90%', maxWidth: '400px', borderRadius: '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}>
        
        {/* Header */}
        <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3f4f6' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>Critérios de desempate</h2>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '2px' }}>{playerData.userName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '4px', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '15px', color: '#374151', fontWeight: 600 }}>Pontos</span>
            <span style={{ fontSize: '16px', fontWeight: 800, color: '#111827' }}>{playerData.points}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>Nº de jogos</span>
              <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>menos é melhor</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>{playerData.matchesPlayed}</span>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>({playerData.matchesWon}-{playerData.matchesLost})</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>Saldo de jogos</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: matchesDiff > 0 ? '#10b981' : (matchesDiff < 0 ? '#ef4444' : '#111827') }}>{matchesDiff > 0 ? `+${matchesDiff}` : matchesDiff}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>Saldo de sets</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: setsDiff > 0 ? '#10b981' : (setsDiff < 0 ? '#ef4444' : '#111827') }}>{setsDiff > 0 ? `+${setsDiff}` : setsDiff}</span>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>({tb.setsWon}-{tb.setsLost})</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>Saldo de games</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: gamesDiff > 0 ? '#10b981' : (gamesDiff < 0 ? '#ef4444' : '#111827') }}>{gamesDiff > 0 ? `+${gamesDiff}` : gamesDiff}</span>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>({tb.gamesWon}-{tb.gamesLost})</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>Sets vencidos</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>{tb.setsWon}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>Games vencidos</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>{tb.gamesWon}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>Saldo de tiebreaks</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: tbDiff > 0 ? '#10b981' : (tbDiff < 0 ? '#ef4444' : '#111827') }}>{tbDiff > 0 ? `+${tbDiff}` : tbDiff}</span>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>({tb.tbWon}-{tb.tbLost})</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>Inscrição</span>
              <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>mais antigo é melhor</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>{formatDate(tb.registrationDate)}</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
